import { Icon, IconsEnum } from 'components/Icons';
import React, { memo, useEffect, useMemo, useRef, useState } from 'react';

import { DashboardCms } from 'lib/datoCms/queries/getDashboardPage';
import { DatoCmsImageTheme } from 'lib/datoCms/types';
import { ProgressBar } from 'components/header/progressBar';
import Script from 'next/script';
import { SkeletonArea } from 'components/skeletonArea';
import classNames from 'classnames';
import { getNonce } from 'lib/policy';
import { setIsOpenPersonalAreaModal } from 'features/modal/modalSlice';
import { setSnaiPlusOpen } from 'features/dashboard/snaiPlus/snaiPlusSlice';
import styles from './SnaiPlusLevel.module.scss';
import { useAppDispatch } from 'lib/centralStore';
import { useCaptainUp } from './useSnaiPlusHooks';
import { useGetLabelByKey } from 'hooks/useLingUI';
import { useIsDarkTheme } from 'hooks/useIsDarkTheme';
import { isMatch } from 'utility/functions';

export type SnaiPlusLevelProps = {
  title?: string;
  logoSnaiPi: DatoCmsImageTheme;
  isUserMenu?: boolean;
  bgColorMenu?: boolean;
  description?: string;
  dashboardPage?: DashboardCms;
  containerClassName?: string;
  handleShowDetailsMenu?: Function;
};

export const SnaiPlusLevel = memo(function SnaiPlusLevel({
  title,
  logoSnaiPi,
  isUserMenu,
  description,
  bgColorMenu = false,
  dashboardPage,
  containerClassName,
  handleShowDetailsMenu,
}: SnaiPlusLevelProps) {
  const { isDarkMode } = useIsDarkTheme();

  const dispatch = useAppDispatch();
  const labelSnaiPlus = useGetLabelByKey('snai-plus');

  const [initialized, setInitialized] = useState(false);
  const prevInitialized = useRef(initialized);
  const { level, points, monete, isReady, isError, nextLevel, start } = useCaptainUp();

  useEffect(() => {
    if (prevInitialized.current !== initialized) {
      prevInitialized.current = initialized;
      if (initialized) {
        start();
      }
    }
  }, [initialized, start]);

  const { logoUrl, logoTitle } = useMemo(() => {
    const { logoDarkMode, logoLightMode } = logoSnaiPi ?? {};
    if (isDarkMode) {
      return {
        logoUrl: logoDarkMode?.url,
        logoTitle: logoDarkMode?.alt ?? 'Logo',
      };
    }

    return {
      logoUrl: logoLightMode?.url,
      logoTitle: logoLightMode?.alt ?? 'Logo',
    };
  }, [isDarkMode, logoSnaiPi]);

  const openCaptainUp = async (): Promise<void> => {
    if (isUserMenu) {
      dispatch(setIsOpenPersonalAreaModal(false));
      if (typeof handleShowDetailsMenu === 'function') {
        handleShowDetailsMenu();
      }
    }
    dispatch(setSnaiPlusOpen(true));
  };

  const objData = useMemo(() => {
    if (isReady) {
      const objLevel = isUserMenu ? null : (
        <div className={styles.loyalty}>
          <Icon iconId={IconsEnum.MEDAL} className={styles.medalIcon} />
          <div className={styles.level}>
            <div className={styles.numberLevel}>
              <span>{dashboardPage?.labelLivello}</span>
              <p>{level}</p>
            </div>
            <div className={styles.coin}>
              <span>{dashboardPage?.labelCoin}</span>
              <p>{monete}</p>
            </div>
          </div>
        </div>
      );

      return (
        <React.Fragment>
          {objLevel}
          <div className={styles.progress}>
            <ProgressBar currencyType={''} progress={points} maxLimit={nextLevel} />
          </div>
        </React.Fragment>
      );
    }

    if (isError && !isUserMenu) {
      return (
        <div className={styles.loyalty}>
          <div>
            <Icon iconId={IconsEnum.MEDAL} className={styles.medalIcon} />
          </div>
          <p className={styles.text}>{description}</p>
        </div>
      );
    }

    if (process.env.NEXT_PUBLIC_CAPTAIN_UP_SDK_URL) {
      return <SkeletonArea internalClassName="noMargin" height="10" />;
    }

    return <React.Fragment key={'nothing'} />;
  }, [
    dashboardPage?.labelCoin,
    dashboardPage?.labelLivello,
    description,
    isError,
    isReady,
    isUserMenu,
    level,
    monete,
    nextLevel,
    points,
  ]);

  return (
    <React.Fragment>
      {isMatch(process.env.NEXT_PUBLIC_CAPTAIN_UP_SDK_URL, '^http') && (
        <Script
          src={process.env.NEXT_PUBLIC_CAPTAIN_UP_SDK_URL}
          key={`url-${initialized}`}
          nonce={getNonce()}
          strategy="lazyOnload"
          onLoad={() => setInitialized(true)}
        />
      )}
      <div
        className={classNames(styles.container, containerClassName ? containerClassName : '', {
          [styles.bgColorMenu]: bgColorMenu,
        })}
      >
        <div className={styles.header}>
          <div className={styles.title}>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={logoUrl} className={styles.logo} alt={logoTitle} title={logoTitle} />
            <span className={styles.cardTitle}>{title ?? labelSnaiPlus}</span>
          </div>
          <button onClick={openCaptainUp} disabled={!isReady}>
            <Icon iconId={IconsEnum.ARROW} color="var(--color-primary)" />
          </button>
        </div>
        {objData}
      </div>
    </React.Fragment>
  );
});
